import React, { useState, useEffect, useCallback, useRef } from 'react';
import { examQuestionBank } from '../../data/index';

const ExamInterface = ({ examId, mode, studentId, onComplete, onExit }) => {
  const [currentExam, setCurrentExam] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const audioRef = useRef(null);

  // 渲染LaTeX數學表達式
  const renderMath = useCallback((text) => {
    if (!text) return '';
    return text; // MathJax會自動處理 \( \) 和 \[ \] 語法
  }, []);

  // 觸發MathJax重新渲染
  useEffect(() => {
    if (window.MathJax && window.MathJax.typesetPromise) {
      window.MathJax.typesetPromise().catch((err) => console.log(err));
    }
  }, [currentQuestionIndex, showExplanation]);

  // 播放音頻
  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(e => {
        console.log('Audio play failed:', e);
        alert('音頻文件暫時不可用。請聯繫管理員或繼續作答。\nAudio file temporarily unavailable. Please contact administrator or continue with the question.');
      });
    }
  };

  // 提交考試
  const handleSubmit = useCallback(() => {
    if (!currentExam) return;

    const endTime = new Date();
    const totalTimeSpent = Math.floor((endTime - startTime) / 1000 / 60); // 分鐘
    
    // 計算成績
    let correctAnswers = 0;
    const detailedAnswers = currentExam.questions.map(question => {
      const userAnswer = answers[question.id];
      const isCorrect = userAnswer?.selectedAnswer === question.correctAnswer;
      if (isCorrect) correctAnswers++;
      
      return {
        questionId: question.id,
        selectedAnswer: userAnswer?.selectedAnswer ?? null,
        correctAnswer: question.correctAnswer,
        isCorrect,
        timeSpent: userAnswer?.timeSpent ?? 0
      };
    });

    const score = Math.round((correctAnswers / currentExam.totalQuestions) * 100);
    const passed = score >= currentExam.passingScore;

    const examRecord = {
      studentId,
      examId,
      mode,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      score,
      totalQuestions: currentExam.totalQuestions,
      correctAnswers,
      timeSpent: totalTimeSpent,
      status: 'completed',
      passed,
      answers: detailedAnswers
    };

    setIsSubmitted(true);
    setShowResults(true);

    // 保存考試記錄（實際應用中會發送到後端）
    console.log('考試記錄:', examRecord);
    
    if (onComplete) {
      onComplete(examRecord);
    }
  }, [currentExam, answers, startTime, studentId, examId, mode, onComplete]);

  // 初始化考試
  useEffect(() => {
    const exam = examQuestionBank.find(e => e.id === examId);
    if (exam) {
      setCurrentExam(exam);
      setStartTime(new Date());
      
      // 設置時間限制（僅限考試模式和預測模式）
      if (mode !== 'studyMode' && exam.modes[mode]?.timeLimit) {
        setTimeRemaining(exam.modes[mode].timeLimit * 60); // 轉換為秒
      }
    }
  }, [examId, mode]);

  // 計時器
  useEffect(() => {
    if (timeRemaining > 0 && !isSubmitted) {
      const timer = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !isSubmitted) {
      // 時間到自動提交
      handleSubmit();
    }
  }, [timeRemaining, isSubmitted, handleSubmit]);

  // 格式化時間顯示
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 處理答案選擇
  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        selectedAnswer: answerIndex,
        timestamp: new Date(),
        timeSpent: Math.floor((new Date() - startTime) / 1000)
      }
    }));

    // 自習模式立即顯示解釋
    if (mode === 'studyMode') {
      setShowExplanation(true);
    }
  };

  // 導航到下一題
  const handleNext = () => {
    if (currentQuestionIndex < currentExam.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    }
  };

  // 導航到上一題
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  // 跳轉到指定題目
  const handleQuestionNavigation = (index) => {
    setCurrentQuestionIndex(index);
    setShowExplanation(false);
  };

  // 結果統計
  const getResultStats = () => {
    if (!currentExam || !isSubmitted) return null;

    const correctAnswers = Object.values(answers).filter((answer, index) => 
      answer.selectedAnswer === currentExam.questions[index]?.correctAnswer
    ).length;

    const score = Math.round((correctAnswers / currentExam.totalQuestions) * 100);
    const passed = score >= currentExam.passingScore;

    return {
      score,
      correctAnswers,
      totalQuestions: currentExam.totalQuestions,
      passed,
      passingScore: currentExam.passingScore
    };
  };

  if (!currentExam) {
    return <div className="exam-loading">載入考試中...</div>;
  }

  const currentQuestion = currentExam.questions[currentQuestionIndex];
  const userAnswer = answers[currentQuestion.id];
  const stats = getResultStats();

  // 結果頁面
  if (showResults && stats) {
    return (
      <div className="exam-results">
        <div className="results-header">
          <h2>考試結果</h2>
          <div className={`result-badge ${stats.passed ? 'passed' : 'failed'}`}>
            {stats.passed ? '通過' : '未通過'}
          </div>
        </div>
        
        <div className="results-stats">
          <div className="stat-item">
            <span className="stat-label">得分</span>
            <span className="stat-value">{stats.score}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">正確答案</span>
            <span className="stat-value">{stats.correctAnswers}/{stats.totalQuestions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">及格分數</span>
            <span className="stat-value">{stats.passingScore}%</span>
          </div>
        </div>

        <div className="results-actions">
          <button onClick={() => setShowResults(false)} className="btn-review">
            查看答案
          </button>
          <button onClick={onExit} className="btn-exit">
            返回
          </button>
        </div>
      </div>
    );
  }

  // 檢測是否為數學考試
  const isMathExam = currentExam && (
    currentExam.category === 'mathematics' || 
    currentExam.name.toLowerCase().includes('mathematics') ||
    currentExam.name.includes('數學')
  );

  // 檢測當前題目是否包含數學表達式
  const currentQuestionHasMath = currentQuestion && (
    currentQuestion.hasMath || 
    (currentQuestion.question && /\\[([\\[]]/g.test(currentQuestion.question)) ||
    (currentQuestion.options && currentQuestion.options.some(opt => /\\[([\\[]]/g.test(opt)))
  );

  return (
    <div 
      className="exam-interface" 
      data-exam-type={isMathExam ? 'mathematics' : 'general'}
    >
      {/* 考試標題和模式 */}
      <div className="exam-header">
        <div className="exam-info">
          <h2>{currentExam.name}</h2>
          <span className="exam-mode">{currentExam.modes[mode].name}</span>
        </div>
        
        <div className="exam-controls">
          {timeRemaining !== null && (
            <div className={`timer ${timeRemaining < 300 ? 'warning' : ''}`}>
              ⏰ {formatTime(timeRemaining)}
            </div>
          )}
          <div className="progress">
            {currentQuestionIndex + 1} / {currentExam.totalQuestions}
          </div>
        </div>
      </div>

      {/* 題目導航 */}
      <div className="question-navigator">
        {currentExam.questions.map((_, index) => (
          <button
            key={index}
            className={`nav-btn ${index === currentQuestionIndex ? 'active' : ''} ${
              answers[currentExam.questions[index].id] ? 'answered' : ''
            }`}
            onClick={() => handleQuestionNavigation(index)}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {/* 題目內容 */}
      <div 
        className="question-content" 
        data-has-math={currentQuestionHasMath}
      >
        <div className="question-header">
          <span className="question-number">第 {currentQuestionIndex + 1} 題</span>
          <span className="question-topic">{currentQuestion.topic}</span>
          <span className={`difficulty ${currentQuestion.difficulty}`}>
            {currentQuestion.difficulty}
          </span>
          {currentQuestionHasMath && (
            <span className="math-indicator">數學</span>
          )}
        </div>
        
        <div className="question-text">
          <div className="question-main">
            <div 
              dangerouslySetInnerHTML={{
                __html: renderMath(currentQuestion.questionZh || currentQuestion.question)
              }}
            />
            {currentQuestion.hasAudio && currentQuestion.audioUrl && (
              <button 
                className="audio-btn"
                onClick={() => playAudio(currentQuestion.audioUrl)}
                title="播放音頻"
              >
                🔊 播放
              </button>
            )}
          </div>
          
          {/* 數學表達式 */}
          {currentQuestion.mathExpression && (
            <div className="math-expression">
              <div dangerouslySetInnerHTML={{
                __html: renderMath(`$${currentQuestion.mathExpression}$`)
              }} />
            </div>
          )}
          
          {/* SVG圖表 */}
          {currentQuestion.svgDiagram && (
            <div className="svg-diagram">
              <div dangerouslySetInnerHTML={{
                __html: currentQuestion.svgDiagram
              }} />
            </div>
          )}
        </div>

        <div className="options">
          {(currentQuestion.optionsZh || currentQuestion.options).map((option, index) => {
            const optionHasMath = /\\[([\\[]]/g.test(option);
            return (
              <button
                key={index}
                className={`option ${userAnswer?.selectedAnswer === index ? 'selected' : ''} ${
                  showExplanation && mode === 'studyMode' 
                    ? (index === currentQuestion.correctAnswer ? 'correct' : 
                       userAnswer?.selectedAnswer === index ? 'incorrect' : '') 
                    : ''
                }`}
                data-has-math={optionHasMath}
                onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                disabled={isSubmitted && mode !== 'studyMode'}
              >
                <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                <span 
                  className="option-text"
                  dangerouslySetInnerHTML={{
                    __html: renderMath(option)
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* 自習模式顯示解釋 */}
        {showExplanation && mode === 'studyMode' && userAnswer && (
          <div className="explanation">
            <h4>解釋</h4>
            <p>{currentQuestion.explanationZh || currentQuestion.explanation}</p>
            {userAnswer.selectedAnswer === currentQuestion.correctAnswer ? (
              <div className="feedback correct">✅ 回答正確！</div>
            ) : (
              <div className="feedback incorrect">
                ❌ 回答錯誤。正確答案是：{String.fromCharCode(65 + currentQuestion.correctAnswer)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 側邊導航箭頭 */}
      <div className="side-navigation">
        <button 
          onClick={handlePrevious} 
          disabled={currentQuestionIndex === 0}
          className="side-nav-btn prev-btn"
          title="上一題"
        >
          ←
        </button>
        
        <button 
          onClick={handleNext} 
          disabled={currentQuestionIndex === currentExam.questions.length - 1}
          className="side-nav-btn next-btn"
          title="下一題"
        >
          →
        </button>
      </div>

      {/* 底部導航按鈕 */}
      <div className="exam-navigation">
        <button 
          onClick={handlePrevious} 
          disabled={currentQuestionIndex === 0}
          className="btn-nav"
        >
          ← 上一題
        </button>
        
        <div className="nav-center">
          {currentQuestionIndex === currentExam.questions.length - 1 ? (
            <button onClick={handleSubmit} className="btn-submit">
              {mode === 'studyMode' ? '完成練習' : '提交考試'}
            </button>
          ) : (
            <button onClick={handleNext} className="btn-nav">
              下一題 →
            </button>
          )}
        </div>
        
        <button onClick={onExit} className="btn-exit">
          退出
        </button>
      </div>
      
            {/* 音頻元素 */}
      <audio ref={audioRef} style={{ display: 'none' }} controls />
    </div>
  );
};

export default ExamInterface; 